// MA-714 — the multiplication factor pair an item's OWN authored fields
// encode, moved here verbatim (behaviour-identical) from
// scripts/audit/arrayVisualIntegrityTest.mjs's private copy (MA-659 slice f)
// so the runtime wrong-tap classifier (classifyMathError.ts) and the
// ms.math.17/18 ceiling guard read ONE derivation instead of two.
//
// The four equal-groups WORD-PROBLEM item types encode the unknown factor
// as a literal `0` in `a`/`b` for the "how many groups?" / "how many in
// each?" question shapes, so any consumer must derive the REAL factor pair
// rather than read the fields verbatim — `a + b` over the raw fields of a
// missing-factor record is the KNOWN factor alone (e.g. ME_M17_102,
// `a: 0, b: 3`), which is exactly the authored "answered the group size
// instead of the number of groups" role-swap distractor, not an additive
// confusion.
//
// Derivation rule per item type (proved against the real corpus in
// docs/build/reports/MA-651-batch15-ms-math-17-remainder-2026-09-20.md
// §0g/§2 and MA-659f's own Phase 0 sweep):
//   - equal_groups_missing_factor, and equal_groups_array_transfer records
//     that ALSO encode a missing factor as a literal 0 (ME_M17_125 is the
//     one live example: itemType is array_transfer but the question is
//     "how many rows?" with a:0) — expectedAnswer IS the derived unknown
//     factor (no arithmetic needed beyond reading the field); the real
//     pair is [expectedAnswer, the other, nonzero field]. Exactly one of
//     a/b must be 0 and the other finite nonzero, or the record's factor
//     pair cannot be derived and the caller must REFUSE it (fail-closed,
//     not a silent pass).
//   - equal_groups_multi_step and equal_groups_array_transfer records
//     with both a/b finite and nonzero: a/b ARE the real multiplication
//     factors (the product, then a single add/subtract step happens
//     outside the pair) — used directly, no derivation needed.
//   - equal_groups_compare: a/b is one authored group-pair from the
//     prompt (sometimes the answer party, sometimes a lure or partial sum)
//     — used directly, same as multi_step/array_transfer. No prompt parser
//     is shipped: it cannot tell from the numbers alone whether the
//     question wants one party's product or the sum of both.
// Returns { a, b } (already Number()-coerced) or null if the record's
// factor pair cannot be derived from its structured fields.

/** The authored fields the derivation reads (a mathExemplars record). */
export interface FactorPairRecord {
  a?: unknown;
  b?: unknown;
  expectedAnswer?: unknown;
}

export function deriveWordProblemFactors(
  item: FactorPairRecord,
): { a: number; b: number } | null {
  const a = Number(item.a);
  const b = Number(item.b);
  const exp = Number(item.expectedAnswer);
  const aIsZero = a === 0;
  const bIsZero = b === 0;
  if (aIsZero || bIsZero) {
    // Missing-factor shape: exactly one of a/b must be the 0 sentinel and
    // the other a finite nonzero known factor, and expectedAnswer (the
    // derived unknown factor) must itself be finite and nonzero.
    if (aIsZero === bIsZero) return null; // both 0, or neither — can't derive
    const known = aIsZero ? b : a;
    if (!Number.isFinite(known) || known <= 0 || !Number.isFinite(exp) || exp <= 0) return null;
    return { a: exp, b: known };
  }
  if (!Number.isFinite(a) || !Number.isFinite(b) || a <= 0 || b <= 0) return null;
  return { a, b };
}
